import { fromJS, List } from 'immutable';
import Actions from 'flow/actions';
import { QueueMember, QueueWithQueueMembers } from 'flow/types/Queue';
import { createTypedMap, TypedMap } from 'flow/types/TypedMap';
import {
    AgentMinimum,
    AgentsMinimum,
    AgentsMinimumPerQueue,
    QueueMinimum,
    QueuesMinimum,
} from 'flow/types/Supervisor';
import { ExtensionKey, QueueKey } from 'flow/types/EntityKeys';

const initialState = fromJS({
    hasSupervisorFeature: false,
    sortingData: {
        queuesMinimum: {},
        agentsMinimum: {},
        agentsMinimumPerQueue: {},
    },
    selected: {
        queue: 'all',
        operatorGroup: 'all',
    },
});

export const supervisorReducer = (state = initialState, action: any) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE: {
            return initialState;
        }
        case Actions.SET_LOGGED_IN_USER_HAS_SUPERVISOR_FEATURE: {
            return state.set('hasSupervisorFeature', action.hasFeature);
        }
        case Actions.SET_SUPERVISOR_SELECTED_QUEUE: {
            return state.setIn(['selected', 'queue'], action.data);
        }
        case Actions.SET_SUPERVISOR_SELECTED_OPERATOR: {
            return state.setIn(['selected', 'operatorGroup'], action.data);
        }
        case Actions.RESULT_QUEUES: {
            const queues: List<TypedMap<QueueWithQueueMembers>> = fromJS(
                action.data,
            );

            type SupervisorSortingData = {
                agentsMinimum: TypedMap<
                    Record<ExtensionKey, TypedMap<AgentMinimum>>
                >;
                agentsPerQueue: TypedMap<
                    Record<
                        QueueKey,
                        TypedMap<Record<ExtensionKey, TypedMap<AgentMinimum>>>
                    >
                >;
                queuesMinimum: TypedMap<
                    Record<QueueKey, TypedMap<QueueMinimum>>
                >;
            };

            const { agentsMinimum, agentsPerQueue, queuesMinimum } =
                queues.reduce<SupervisorSortingData>(
                    (accumulator, queue) => {
                        const agentsSubMap = queue
                            .get('queueMembers')
                            ?.reduce((memberAccumulator, member) => {
                                const extensionKey = member.get('extensionKey');
                                if (extensionKey) {
                                    accumulator.agentsMinimum =
                                        accumulator.agentsMinimum.set(
                                            extensionKey,
                                            getMinimumAgent(member),
                                        );

                                    memberAccumulator = memberAccumulator.set(
                                        extensionKey,
                                        getMinimumAgent(member),
                                    );
                                }
                                return memberAccumulator;
                            }, createTypedMap<AgentsMinimum>());

                        const queueKey = queue.get('key');
                        accumulator.queuesMinimum =
                            accumulator.queuesMinimum.set(
                                queueKey,
                                getMinimumQueue(queue),
                            );
                        accumulator.agentsPerQueue =
                            accumulator.agentsPerQueue.set(
                                queueKey,
                                agentsSubMap,
                            );

                        return accumulator;
                    },
                    {
                        agentsMinimum: createTypedMap<AgentsMinimum>(),
                        agentsPerQueue: createTypedMap<AgentsMinimumPerQueue>(),
                        queuesMinimum: createTypedMap<QueuesMinimum>(),
                    },
                );

            return state
                .setIn(['sortingData', 'queuesMinimum'], queuesMinimum)
                .setIn(['sortingData', 'agentsMinimum'], agentsMinimum)
                .setIn(
                    ['sortingData', 'agentsMinimumPerQueue'],
                    agentsPerQueue,
                );
        }
        default: {
            return state;
        }
    }
};

export default supervisorReducer;

const getMinimumAgent = (agent: TypedMap<QueueMember>) =>
    fromJS({
        extensionKey: agent.get('extensionKey'),
        firstName: agent.getIn(['contact', 'firstName']),
        lastName: agent.getIn(['contact', 'lastName']),
    });

const getMinimumQueue = (queue: TypedMap<QueueWithQueueMembers>) =>
    fromJS({
        key: queue.get('key'),
        description: queue.get('description'),
    });
